import React, { useEffect, useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from "@mui/material"
import EmailOutlined from "@mui/icons-material/EmailOutlined"
import LockOutlined from "@mui/icons-material/LockOutlined"
import LoginOutlined from "@mui/icons-material/LoginOutlined"
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import apiClient, { acceptLogin } from "../api/apiClient"
import useApi from "../hooks/useApi"
import { showModal } from "../redux/modalSlice"
import Footer from "../components/Footer"
import "react-toastify/dist/ReactToastify.css"

const TRUSTED_DEVICE_TOKEN_KEY = "petbioTrustedDeviceToken"

const loginFieldSx = {
  "& .MuiInputLabel-root": {
    color: "#5F6876",
    fontWeight: 600
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3E4755"
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
    color: "#111827",
    minHeight: 54,
    transition: "background-color 160ms ease, box-shadow 160ms ease"
  },
  "& .MuiOutlinedInput-root:hover": {
    backgroundColor: "#FFFFFF"
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: "#FFFFFF",
    boxShadow: "0 0 0 4px rgba(62, 71, 85, 0.08)"
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D8DDE6"
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#AEB6C3"
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#3E4755",
    borderWidth: 1
  }
}

const inputIconSx = {
  color: "#6B7280",
  fontSize: 22
}

const submitButtonSx = {
  backgroundColor: "#3E4755",
  borderRadius: "999px",
  boxShadow: "0 12px 24px rgba(15, 23, 42, 0.18)",
  fontWeight: 800,
  letterSpacing: 0,
  minHeight: 48,
  minWidth: { xs: "100%", sm: 168 },
  px: 3.5,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#2F3744",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.24)"
  },
  "&.Mui-disabled": {
    backgroundColor: "#6B7280",
    color: "#FFFFFF"
  }
}

function Login() {
  const [data, setData] = useState({
    email: "",
    password: ""
  })
  const [twoFactor, setTwoFactor] = useState({
    required: false,
    challengeToken: "",
    email: "",
    code: "",
    rememberDevice: true
  })
  const [load, setLoad] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isLoggedIn, userType } = useSelector(state => state.auth)
  const { request } = useApi(data => apiClient.post("/auth/login", data))

  useEffect(() => {
    if (isLoggedIn) navigation(userType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userType])

  const handleChange = (key, value) => {
    setData({ ...data, [key]: value })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (load) return
    setLoad(true)

    const result = await request({
      ...data,
      trustedDeviceToken: localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY)
    })

    if (!result.ok) {
      dispatch(
        showModal({
          error: true,
          title: "Login",
          message: result.data?.message || "Could not sign in. Please try again."
        })
      )
      setLoad(false)
      return
    }

    if (result.data?.twoFactorRequired) {
      setTwoFactor({
        required: true,
        challengeToken: result.data.challengeToken,
        email: result.data.email,
        code: "",
        rememberDevice: true
      })
      setLoad(false)
      return
    }

    finishLogin(result.data)
  }

  async function handleTwoFactorSubmit(e) {
    e.preventDefault()
    if (load) return
    setLoad(true)

    const result = await apiClient.post("/auth/2fa/login/verify", {
      challengeToken: twoFactor.challengeToken,
      code: twoFactor.code,
      rememberDevice: twoFactor.rememberDevice
    })

    if (!result.ok) {
      dispatch(
        showModal({
          error: true,
          title: "Login verification",
          message: result.data?.message || "Could not verify your login. Please try again."
        })
      )
      setLoad(false)
      return
    }

    finishLogin(result.data)
  }

  const finishLogin = result => {
    setLoad(false)
    if (!acceptLogin(result)) {
      dispatch(showModal({ error: true, title: "Login", message: "Could not complete your login. Please try again." }))
      return
    }
    navigation(result.data.role)
  }

  const navigation = role => {
    if (role === "AD" || role === "SA") {
      navigate("/admin/dashboard")
    } else if (["PPTN", "TN", "EXP"].includes(role)) {
      navigate("/tn/dashboard")
    }
  }

  if (isLoggedIn) {
    return null
  }

  return (
    <>
      <Box className="container gx-0 layout" sx={{ py: { xs: 2, md: 4 } }}>
        <Grid
          container
          spacing={0}
          sx={{
            borderRadius: { xs: 0, md: "40px" },
            boxShadow: "0 22px 52px rgba(15, 23, 42, 0.14)",
            overflow: "hidden"
          }}
        >
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundColor: "#FFFFFF",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: { xs: "auto", md: 640 },
              p: { xs: 3, md: 5 }
            }}
          >
            <Typography
              component="blockquote"
              sx={{
                color: "#8B7477",
                fontSize: { xs: "0.95rem", md: "1rem" },
                lineHeight: 1.55,
                maxWidth: 620,
                mx: "auto",
                textAlign: "center"
              }}
            >
              "Our pet intolerance test is the road map to pet health. We strive to make the best
              herbal products for pets and have exposure to as many pet parents as possible, so we
              can be the first choice rather than the last resort."
              <Typography component="span" sx={{ display: "block", mt: 0.75 }}>
                - Debe Gwynn
              </Typography>
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 3, md: 4 } }}>
              <Box sx={{ maxWidth: 500, width: "100%" }}>
                <Box
                  component="img"
                  src={require("../assets/pet_signin-removebg-preview.png")}
                  alt="Pet intolerance test kit"
                  sx={{ display: "block", width: "100%" }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid
            component={Paper}
            elevation={0}
            item
            xs={12}
            md={6}
            sx={{
              backgroundColor: "#FFFFFF",
              borderLeft: { md: "1px solid #EEF1F5" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: { xs: "auto", md: 640 },
              p: { xs: 3, md: 5 }
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Box sx={{ width: { xs: "11rem", md: "13rem" } }}>
                <Box
                  component="img"
                  src={require("../assets/logo-warm-purple.png")}
                  alt="Glacier Peak Holistics"
                  sx={{ display: "block", width: "100%" }}
                />
              </Box>
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#111827",
                fontSize: { xs: "1.6rem", md: "1.9rem" },
                fontWeight: 800,
                letterSpacing: 0,
                mb: 1,
                textAlign: "center"
              }}
            >
              Glacier Peak Biofeedback Center
            </Typography>
            <Typography
              sx={{
                color: "#4B5563",
                fontSize: "0.98rem",
                mb: { xs: 2.5, md: 3.5 },
                textAlign: "center"
              }}
            >
              Please fill in your email and password to enter
            </Typography>

            <Box
              component="form"
              onSubmit={twoFactor.required ? handleTwoFactorSubmit : handleSubmit}
              sx={{ mx: "auto", width: "100%", maxWidth: 680 }}
            >
              {!twoFactor.required ? (
                <>
                  <Box sx={{ mb: 2.25 }}>
                    <TextField
                      fullWidth
                      id="email"
                      label="Email"
                      value={data.email}
                      onChange={e => handleChange("email", e.target.value)}
                      variant="outlined"
                      required
                      sx={loginFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined sx={inputIconSx} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 2.25 }}>
                    <TextField
                      fullWidth
                      type="password"
                      id="password"
                      label="Password"
                      value={data.password}
                      onChange={e => handleChange("password", e.target.value)}
                      variant="outlined"
                      required
                      sx={loginFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined sx={inputIconSx} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Typography sx={{ color: "#4B5563", mb: 2 }}>
                    Enter the verification code sent to {twoFactor.email || "your 2FA email"}.
                  </Typography>
                  <Box sx={{ mb: 2.25 }}>
                    <TextField
                      fullWidth
                      id="two-factor-code"
                      label="Verification code"
                      value={twoFactor.code}
                      onChange={e =>
                        setTwoFactor({ ...twoFactor, code: e.target.value.replace(/\D/g, "") })
                      }
                      inputProps={{ maxLength: 6, inputMode: "numeric" }}
                      variant="outlined"
                      required
                      sx={loginFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VerifiedUserOutlined sx={inputIconSx} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                  <FormControlLabel
                    sx={{ color: "#4B5563", mb: 2 }}
                    control={
                      <Checkbox
                        checked={twoFactor.rememberDevice}
                        onChange={e =>
                          setTwoFactor({ ...twoFactor, rememberDevice: e.target.checked })
                        }
                      />
                    }
                    label="Remember this browser for 30 days"
                  />
                  <Button
                    disabled={load}
                    onClick={() => setTwoFactor({ required: false, challengeToken: "", email: "", code: "", rememberDevice: true })}
                  >
                    Back to login
                  </Button>
                </>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  disabled={load}
                  endIcon={
                    !load && (twoFactor.required ? <VerifiedUserOutlined /> : <LoginOutlined />)
                  }
                  sx={submitButtonSx}
                  type="submit"
                  variant="contained"
                >
                  {load ? (
                    <CircularProgress color="inherit" size={22} />
                  ) : twoFactor.required ? (
                    "Verify"
                  ) : (
                    "Login"
                  )}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Footer />
    </>
  )
}

export default Login
